/**
 * React Query bindings. One place that knows how the cache is keyed, so an
 * invalidation after a write cannot be forgotten in a component.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { api, type AuditParams, type ListParams } from './index'
import type {
  AccessChange,
  AccessResponse,
  AuditResponse,
  ConfigRow,
  ListResponse,
  SchemaResponse,
} from './types'

export const queryKeys = {
  schema: ['schema'] as const,
  list: (type: string, params: ListParams) => ['config', type, 'list', params] as const,
  row: (type: string, key: string) => ['config', type, 'row', key] as const,
  history: (type: string, key: string) => ['config', type, 'history', key] as const,
  availableKeys: (type: string) => ['config', type, 'available-keys'] as const,
  audit: (params: AuditParams) => ['audit', params] as const,
  permissions: ['permissions'] as const,
}

/**
 * The schema drives navigation and every form, so it is fetched once and held.
 * `staleTime: Infinity` is deliberate — a config type appearing mid-session
 * would reshuffle the navigation under the user. A reload picks it up.
 */
export function useSchema(options?: Partial<UseQueryOptions<SchemaResponse>>) {
  return useQuery<SchemaResponse>({
    queryKey: queryKeys.schema,
    queryFn: () => api.getSchema(),
    staleTime: Infinity,
    retry: (failureCount, error) => {
      // No point retrying a 401/403 — it will not resolve itself.
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403) return false
      return failureCount < 2
    },
    ...options,
  })
}

/**
 * First sign-in: pick a region. The response IS the new schema, so it replaces
 * the cached one directly rather than refetching.
 */
export function useRegisterRegion() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (region: string) => api.registerRegion(region),
    onSuccess: (schema) => client.setQueryData(queryKeys.schema, schema),
  })
}

export function useConfigList(type: string, params: ListParams, enabled = true) {
  return useQuery<ListResponse>({
    queryKey: queryKeys.list(type, params),
    queryFn: () => api.list(type, params),
    enabled: enabled && Boolean(type),
    placeholderData: (previous) => previous, // No flash of empty table while filtering.
  })
}

export function useConfigRow(type: string, key: string | null) {
  return useQuery<ConfigRow>({
    queryKey: queryKeys.row(type, key ?? ''),
    queryFn: () => api.get(type, key as string),
    enabled: Boolean(type && key),
  })
}

/**
 * Keys still free for a new row. Under the type's cache prefix, so saving a row
 * (which invalidates ['config', type]) drops the one just used.
 */
export function useAvailableKeys(type: string, enabled: boolean) {
  return useQuery<{ keys: string[] }>({
    queryKey: queryKeys.availableKeys(type),
    queryFn: () => api.availableKeys(type),
    enabled: enabled && Boolean(type),
    staleTime: 0,
  })
}

export function useRowHistory(type: string, key: string | null, enabled = true) {
  return useQuery<AuditResponse>({
    queryKey: queryKeys.history(type, key ?? ''),
    queryFn: () => api.history(type, key as string),
    enabled: enabled && Boolean(type && key),
  })
}

export function useAudit(params: AuditParams) {
  return useQuery<AuditResponse>({
    queryKey: queryKeys.audit(params),
    queryFn: () => api.audit(params),
  })
}

export function useSaveRow(type: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ key, values }: { key: string; values: ConfigRow }) =>
      api.put(type, key, values),
    onSuccess: (_row, variables) => {
      // A write changes the list, the row, its history and the global audit
      // feed. Invalidating the type prefix catches the first three at once.
      void client.invalidateQueries({ queryKey: ['config', type] })
      void client.invalidateQueries({ queryKey: ['audit'] })
      void client.invalidateQueries({ queryKey: queryKeys.history(type, variables.key) })
    },
  })
}

export function useDeleteRow(type: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => api.remove(type, key),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['config', type] })
      void client.invalidateQueries({ queryKey: ['audit'] })
    },
  })
}

export function usePermissions() {
  return useQuery<AccessResponse>({
    queryKey: queryKeys.permissions,
    queryFn: () => api.listPermissions(),
  })
}

export function useSavePermission() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userOid, change }: { userOid: string; change: AccessChange }) =>
      api.putPermission(userOid, change),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.permissions })
      void client.invalidateQueries({ queryKey: ['audit'] })
      // The caller's own grants decide which screens exist, so the schema has
      // to be refetched when permissions change.
      void client.invalidateQueries({ queryKey: queryKeys.schema })
    },
  })
}

export function useDeletePermission() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userOid: string) => api.deletePermission(userOid),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.permissions })
      void client.invalidateQueries({ queryKey: ['audit'] })
      void client.invalidateQueries({ queryKey: queryKeys.schema })
    },
  })
}
